import { LucideIcon, ArrowRight, AlertTriangle, Hourglass, Radio } from "lucide-react";
import { Link } from "react-router-dom";
import { StatusCounts } from "features/dashboard/api/DashboardApi";
import { daysAgo } from "features/linkedin-jobs/utils";

export const JOB_PIPELINE_KEYS = ["linkedin_alerts", "theirstack_jobs", "watchlist_leads"] as const;

export const PIPELINE_LABELS: Record<string, string> = {
  linkedin_alerts: "LinkedIn Alerts",
  theirstack_jobs: "Other Jobs",
  watchlist_leads: "Watchlist Leads",
  companies: "Companies",
  contacts: "Contacts",
};

const STATUS_ORDER = [
  "new", "reviewing", "applied", "interviewing",
  "offer_received", "hired", "rejected", "not_relevant", "archived",
];

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  reviewing: "Reviewing",
  applied: "Applied",
  interviewing: "Interviewing",
  offer_received: "Offer",
  hired: "Hired",
  rejected: "Rejected",
  not_relevant: "Not Relevant",
  archived: "Archived",
};

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  reviewing: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  applied: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
  interviewing: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  offer_received: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  hired: "bg-emerald-600/15 text-emerald-300 border-emerald-600/25",
  rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  not_relevant: "bg-slate-700/40 text-slate-400 border-slate-600/40",
  archived: "bg-slate-700/40 text-slate-500 border-slate-600/40",
};

/** The first/headline stat in a row renders as a "hero" card — a navy panel
 * (same color as the app's top bar in both themes) that pops against the
 * surrounding white cards, mirroring the reference dashboard's lead metric. */
export function StatCard({
  icon: Icon,
  label,
  value,
  hero = false,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  hero?: boolean;
}) {
  if (hero) {
    return (
      <div className="rounded-xl bg-topbar p-5 flex flex-col justify-between min-h-[104px]">
        <div className="flex items-center justify-between">
          <p className="text-sm text-topbar-muted-foreground font-inter">{label}</p>
          <div className="w-8 h-8 rounded-lg bg-topbar-foreground/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-topbar-foreground" />
          </div>
        </div>
        <p className="text-3xl font-bold font-inter text-topbar-foreground mt-2">{value}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-5 flex flex-col justify-between min-h-[104px]">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground font-inter">{label}</p>
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
      <p className="text-3xl font-bold font-inter text-foreground mt-2">{value}</p>
    </div>
  );
}

export function StatusBadgeRow({ pipelineKey, counts }: { pipelineKey: string; counts: StatusCounts }) {
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const nonZero = STATUS_ORDER.filter((s) => counts[s] > 0);

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-b-0">
      <span className="text-xs font-semibold text-foreground font-inter w-32 shrink-0">
        {PIPELINE_LABELS[pipelineKey] ?? pipelineKey}
      </span>
      <span className="text-xs text-muted-foreground font-inter w-14 shrink-0">{total} total</span>
      {nonZero.length === 0 ? (
        <span className="text-xs text-muted-foreground font-inter italic">No jobs yet</span>
      ) : (
        <div className="flex items-center gap-1.5 flex-wrap">
          {nonZero.map((status) => (
            <span
              key={status}
              className={`text-[10px] px-2 py-0.5 rounded-full border font-inter font-medium ${STATUS_COLORS[status]}`}
            >
              {STATUS_LABELS[status]} {counts[status]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** action = routine backlog with a clear one-click fix (amber); urgent = aging/at-risk
 * items actively decaying the longer they're ignored (rose); system = an ops/pipeline
 * health signal that isn't the viewer's fault to click through, just to know about (sky). */
const ALERT_VARIANTS = {
  action: { icon: AlertTriangle, badge: "bg-amber-500/15 border-amber-500/25 text-amber-400", accent: "border-l-amber-500" },
  urgent: { icon: Hourglass, badge: "bg-rose-500/15 border-rose-500/25 text-rose-400", accent: "border-l-rose-500" },
  system: { icon: Radio, badge: "bg-sky-500/15 border-sky-500/25 text-sky-400", accent: "border-l-sky-500" },
} as const;

export function AlertBanner({
  children,
  to,
  linkLabel = "View",
  variant = "action",
}: {
  children: React.ReactNode;
  to?: string;
  linkLabel?: string;
  variant?: keyof typeof ALERT_VARIANTS;
}) {
  const { icon: Icon, badge, accent } = ALERT_VARIANTS[variant];
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-border border-l-4 ${accent} bg-surface hover:border-muted-foreground/30 transition-colors`}>
      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${badge}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 text-sm text-foreground font-inter leading-snug">{children}</div>
      {to && (
        <Link
          to={to}
          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium shrink-0 whitespace-nowrap transition-colors"
        >
          {linkLabel}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}

export function formatLastActivity(dateStr: string | null): string {
  if (!dateStr) return "No activity yet";
  return daysAgo(dateStr);
}
